class ContactFormWrapper extends HTMLElement {
  static observedAttributes = ["endpoint"];

  constructor() {
    super();
    this._onSubmit = this._onSubmit.bind(this);
    this._onFileChange = this._onFileChange.bind(this);

    this.maxBytes = 2 * 1024 * 1024; // 2MB
    this.allowedExt = [".jpg", ".jpeg", ".png"];
  }

  get endpoint() {
    return "https://hunni-serverless.vercel.app/api/bero/contact-us";
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    if (!this.form) {
      throw new Error("<contact-form-wrapper> requires a <form> inside it.");
    }

    this.fileInput = this.form.querySelector('input[type="file"][name="file"]');
    this.fileInputTextTarget =
      this.form.querySelector(".file-text-target") || null;
    this.statusEl = this.querySelector("[data-contact-status]");
    this.fileNameHintSpan = this.form.querySelector("#contact-file")
      ? this.form.querySelector('label[for="contact-file"]') // not ideal
      : null;

    this.form.addEventListener("submit", this._onSubmit);
    this.fileInput?.addEventListener("change", this._onFileChange);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this._onSubmit);
    this.fileInput?.removeEventListener("change", this._onFileChange);
  }

  _setStatus(type, msg) {
    if (!this.statusEl) return;
    this.statusEl.textContent = msg || "";
    this.statusEl.dataset.type = type || "";
  }

  _setSubmitting(isSubmitting) {
    const btn = this.form.querySelector('button[type="submit"]');
    if (!btn) return;
    btn.disabled = !!isSubmitting;
    btn.dataset.loading = isSubmitting ? "true" : "false";
  }

  _validateFile(file) {
    if (!file) return { ok: true };

    if (file.size > this.maxBytes) {
      return { ok: false, error: "File is too large. Maximum size is 2MB." };
    }

    const name = (file.name || "").toLowerCase();
    const okExt = this.allowedExt.some((ext) => name.endsWith(ext));
    if (!okExt) {
      return {
        ok: false,
        error: "Invalid file type. Allowed: .jpg, .jpeg, .png",
      };
    }

    return { ok: true };
  }

  _onFileChange(e) {
    const file = e.target.files?.[0] || null;
    const v = this._validateFile(file);

    if (!v.ok) {
      e.target.value = "";
      this._setStatus("error", v.error);
      return;
    }

    if (this.fileInputTextTarget) {
      this.fileInputTextTarget.textContent = file
        ? file.name
        : "Maximum of 2MB";
      this.fileInputTextTarget.classList.remove("text-black/50");
    }

    this._setStatus("", "");
  }

  async _onSubmit(e) {
    e.preventDefault();
    this._setStatus("", "");

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const file = this.fileInput?.files?.[0] || null;
    const vf = this._validateFile(file);
    if (!vf.ok) {
      this._setStatus("error", vf.error);
      return;
    }

    const formData = new FormData(this.form);

    if (file) formData.set("attachment_file", file);
    formData.delete("file");

    this._setSubmitting(true);

    try {
      const res = await fetch(this.endpoint, {
        method: "POST",
        body: formData,
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        this._setStatus(
          "error",
          json?.error || `Request failed (${res.status})`
        );
        return;
      }

      this._setStatus(
        "success",
        "Thank you! We received your message and will get back to you as soon as possible."
      );
      this.form.reset();
    } catch (err) {
      this._setStatus("error", "Network error. Please try again.");
    } finally {
      this._setSubmitting(false);
    }
  }
}

customElements.define("contact-form-wrapper", ContactFormWrapper);
